import { useEffect, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";

const WA_NUMBER = "436641004293"; // +43 664 1004293
const WA_DISPLAY = "+43 664 1004293";
const WA_TEXT = encodeURIComponent(
  "Hallo Waltraud! Ich habe eine Frage zu meiner Buchung."
);
const WA_HREF = `https://wa.me/${WA_NUMBER}?text=${WA_TEXT}`;

export default function WhatsAppFab() {
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);

  // Click-outside + Escape to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={cardRef} className="wa-fab fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3">
      {/* Popup card — appears above the FAB */}
      {open && (
        <div
          data-testid="whatsapp-popup"
          className="w-[320px] sm:w-[360px] bg-[hsl(158_28%_16%)] border border-white/12 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)] overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-300"
          style={{ borderRadius: "18px" }}
        >
          {/* Header — green WhatsApp band */}
          <div className="bg-[#128C7E] text-white px-5 py-4 flex items-center gap-3">
            <div className="relative shrink-0 w-11 h-11 rounded-full bg-white/95 flex items-center justify-center text-[#128C7E] font-serif font-semibold text-lg">
              W
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#25D366] border-2 border-[#128C7E]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] leading-tight">Waltraud Fernberg</div>
              <div className="text-[11px] text-white/85 mt-0.5 tracking-wide">
                Chalet Steiermark · Gastgeberin
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="whatsapp-popup-close"
              aria-label="Schließen"
              className="w-8 h-8 -mr-2 rounded-full hover:bg-white/15 inline-flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>

          {/* Message bubble */}
          <div className="px-5 py-5 bg-[hsl(158_20%_13%)]">
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 mb-2">
              Persönliche Nachricht
            </div>
            <p className="text-[14px] leading-relaxed text-white/95">
              Du hast Fragen zu deiner Buchung, deinem Aufenthalt oder zur Begleitung?
              <span className="block mt-2">
                Schreib mir einfach direkt per WhatsApp. Ich melde mich so schnell wie möglich persönlich bei dir.
              </span>
              <span className="block mt-3 italic text-white/70">— Waltraud</span>
            </p>
            <div className="mt-4 flex items-center gap-2 text-[11px] text-white/55">
              <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse" />
              <span>Antwortet üblicherweise in wenigen Minuten</span>
            </div>
          </div>

          {/* CTA */}
          <a
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-popup-open"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1FBA5A] text-white px-5 py-4 transition-colors group"
          >
            <span className="inline-flex items-center gap-3">
              <MessageCircle className="w-4 h-4" strokeWidth={2.25} />
              <span className="text-[12px] tracking-[0.18em] uppercase font-semibold">Chat starten</span>
            </span>
            <span className="text-[12px] font-semibold tabular-nums">{WA_DISPLAY}</span>
          </a>
        </div>
      )}

      {/* Trigger button — pill with number */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-fab"
        aria-label={open ? "WhatsApp schließen" : `WhatsApp öffnen · ${WA_DISPLAY}`}
        aria-expanded={open}
        className="group inline-flex items-center gap-3 bg-[#25D366] text-white pl-4 pr-5 py-3 shadow-2xl hover:shadow-[0_20px_60px_-15px_rgba(37,211,102,0.6)] transition-all duration-300 hover:-translate-y-0.5"
        style={{ borderRadius: "999px" }}
      >
        <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
          {!open && <span className="absolute inset-0 rounded-full bg-white/40 animate-ping opacity-60" />}
          <MessageCircle className="relative w-4 h-4" strokeWidth={2.25} />
        </span>
        <span className="inline-flex items-baseline gap-2 whitespace-nowrap leading-none">
          <span className="text-[10px] tracking-[0.22em] uppercase font-semibold opacity-90">WhatsApp</span>
          <span className="text-[11px] font-semibold tracking-wide tabular-nums">{WA_DISPLAY}</span>
        </span>
      </button>
    </div>
  );
}
